import { CheckCircleFilled, ExclamationCircleFilled } from "@ant-design/icons";
import { AppPaths } from "@p0security/shared/routes/constants";
import { ruleComponents } from "@p0security/shared/types/workflow/constants";
import { RoutingRule } from "@p0security/shared/types/workflow/types";
import { Alert, App, Flex, List, Spin, Tooltip, Typography } from "antd";
import { ErrorDisplay } from "components/Error";
import yaml from "js-yaml";
import stringify from "json-stable-stringify";
import { isEqual, isUndefined, omitBy } from "lodash";
import React, {
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useRoutingRulesStore } from "store/routingRulesStore";

import CodeEditor from "../Jit/Routing/CodeEditor";
import { AccessEditor } from "../Jit/Routing/components/editors/AccessEditor";
import { RequestorEditor } from "../Jit/Routing/components/editors/RequestorEditor";
import { ResourceEditor } from "../Jit/Routing/components/editors/ResourceEditor";
import {
  DEFAULT_NEW_REQUESTOR,
  DEFAULT_NEW_RESOURCE,
} from "../Jit/Routing/constants";
import { InstalledIntegrationsProvider } from "../Jit/Routing/providers/InstalledIntegrationsProvider";
import {
  RoutingRuleEditorContext,
  RoutingRuleEditorProvider,
} from "../Jit/Routing/store/RoutingRuleEditorContext";
import { useLoadRoutingRules } from "../Jit/Routing/useLoadRoutingRules";
import { useSubmitWorkflow } from "../Jit/Routing/useSubmitWorkflow";
import { DEFAULT_RULE, EXAMPLE_RULE, hashRule } from "../Jit/Routing/utils";
import { PolicyEditorHeader } from "./PolicyEditorHeader";
import { PolicyOutlineNav } from "./PolicyOutlineNav";
import { SECTION_TITLES } from "./constants";

const Section: React.FC<{
  title: string;
  children: React.ReactNode;
  valid: boolean;
  touched: boolean;
}> = ({ title, children, valid, touched }) => {
  const borderStyle = touched && !valid ? "border-[#cf1322]" : undefined;
  return (
    <List.Item className={`py-4! max-w-200 ${borderStyle}`}>
      <Flex align="flex-start" vertical gap={12} className="w-full">
        <Flex
          align="center"
          gap={8}
          className="text-[#262626] text-sm font-semibold mb-5"
        >
          {title}
          {touched && (
            <Tooltip title={valid ? "Complete" : `${title} needs attention`}>
              <Typography.Text
                type={valid ? "success" : "danger"}
                className="text-sm inline-flex"
              >
                {valid ? <CheckCircleFilled /> : <ExclamationCircleFilled />}
              </Typography.Text>
            </Tooltip>
          )}
        </Flex>
        {children}
      </Flex>
    </List.Item>
  );
};

export type EditorMode = "advanced" | "guided";
export type SectionKey = (typeof ruleComponents)[number];

type PolicyEditorPageInnerProps = {
  initialRule?: RoutingRule;
  ruleIndex?: number;
};

const PolicyEditorPageInner: React.FC<PolicyEditorPageInnerProps> = ({
  initialRule,
  ruleIndex,
}) => {
  const navigate = useNavigate();
  const { orgSlug } = useParams();
  const { modal } = App.useApp();
  const submitWorkflow = useSubmitWorkflow();
  const {
    currentVersion,
    errors,
    isSubmitting,
    jsonRules,
    selectedYamlRule,
    setErrors,
    setSelectedRule,
  } = useRoutingRulesStore();

  const {
    name,
    disabled,
    resource,
    requestor,
    approval,
    setName,
    setDisabled,
    isValidRule,
    isComponentValid,
  } = useContext(RoutingRuleEditorContext);

  const isEditMode = initialRule !== undefined;

  // useLayoutEffect, not useEffect — seed the store before paint to avoid a
  // flash of empty YAML when toggling to advanced mode.
  useLayoutEffect(() => {
    setSelectedRule(initialRule ?? {});
  }, [initialRule, setSelectedRule]);

  const [mode, setMode] = useState<EditorMode>("guided");

  const [yamlDirty, setYamlDirty] = useState(false);
  const [resourceFilterEditInProgress, setResourceFilterEditInProgress] =
    useState(false);
  const [accessRuleEditInProgress, setAccessRuleEditInProgress] =
    useState(false);

  const hasFormChanges = useMemo(() => {
    if (!isEditMode) {
      return !!(
        name ||
        disabled ||
        (resource && !isEqual(resource, DEFAULT_NEW_RESOURCE)) ||
        (requestor && !isEqual(requestor, DEFAULT_NEW_REQUESTOR)) ||
        (approval && approval.length > 0)
      );
    }
    return !isEqual(
      omitBy(
        {
          ...initialRule,
          name: initialRule.name || undefined,
          disabled: initialRule.disabled ?? false,
        },
        isUndefined
      ),
      omitBy(
        {
          name: name || undefined,
          disabled: disabled ?? false,
          requestor,
          resource,
          approval,
        },
        isUndefined
      )
    );
  }, [isEditMode, initialRule, name, disabled, requestor, resource, approval]);

  const isDirty = yamlDirty || hasFormChanges;
  const isEditing = resourceFilterEditInProgress || accessRuleEditInProgress;

  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (isDirty || isEditing) {
        e.preventDefault();
      }
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [isDirty, isEditing]);

  const handleCancel = useCallback(() => {
    const exit = () => navigate(`/o/${orgSlug}/${AppPaths.Policies}`);
    if (isDirty || isEditing) {
      modal.confirm({
        title: "You have unsaved changes",
        content: "Are you sure you want to leave? Your changes will be lost.",
        okText: "Discard",
        okType: "danger",
        cancelText: "Keep editing",
        onOk: exit,
      });
    } else {
      exit();
    }
  }, [isDirty, isEditing, modal, navigate, orgSlug]);

  const handleModeChange = useCallback(
    (value: EditorMode) => {
      if (value === "advanced") {
        if (requestor && resource && approval && approval.length > 0) {
          setSelectedRule({
            name: name || undefined,
            disabled: disabled ?? false,
            requestor,
            resource,
            approval,
          });
        } else if (initialRule) {
          setSelectedRule(initialRule);
        }
      }
      setMode(value);
    },
    [
      approval,
      disabled,
      initialRule,
      name,
      requestor,
      resource,
      setSelectedRule,
    ]
  );

  const setDefaultRule = useCallback(() => {
    setSelectedRule(DEFAULT_RULE);
  }, [setSelectedRule]);

  const setSampleExample = useCallback(() => {
    setSelectedRule(EXAMPLE_RULE);
  }, [setSelectedRule]);

  const upsertRule = useCallback(
    async (rule: RoutingRule) => {
      const ruleHash = stringify(rule);
      const updatedRules = [...jsonRules];

      if (isEditMode && ruleIndex !== undefined) {
        if (
          updatedRules.some(
            (r, i) => i !== ruleIndex && stringify(r) === ruleHash
          )
        ) {
          setErrors(["This rule already exists"]);
          return false;
        }
        updatedRules[ruleIndex] = rule;
      } else {
        if (jsonRules.some((r) => stringify(r) === ruleHash)) {
          setErrors(["This rule already exists"]);
          return false;
        }
        updatedRules.push(rule);
      }

      const policyLabel = rule.name ? `Policy "${rule.name}"` : "Policy";
      try {
        await submitWorkflow({
          jsonData: { currentVersion, workflow: { rules: updatedRules } },
          shouldNavigate: false,
          successMessage: `${policyLabel} was successfully saved.`,
        });
        return useRoutingRulesStore.getState().errors.length === 0;
      } catch (err) {
        setErrors([err instanceof Error ? err.message : String(err)]);
        return false;
      }
    },
    [
      isEditMode,
      ruleIndex,
      jsonRules,
      currentVersion,
      submitWorkflow,
      setErrors,
    ]
  );

  const handleSave = useCallback(async () => {
    if (!isValidRule || !resource || !requestor || !approval) return;
    const rule = { name, disabled, requestor, resource, approval };
    const success = await upsertRule(rule);
    if (!success) return;
    navigate(`/o/${orgSlug}/${AppPaths.Policies}`);
  }, [
    isValidRule,
    name,
    disabled,
    resource,
    requestor,
    approval,
    upsertRule,
    navigate,
    orgSlug,
  ]);

  const handleYamlSubmit = useCallback(
    async (yamlString: string) => {
      let parsed: unknown;
      try {
        parsed = yaml.load(yamlString);
      } catch (err) {
        setErrors([`YAML parse error: ${String(err)}`]);
        return;
      }
      if (
        !parsed ||
        typeof parsed !== "object" ||
        Array.isArray(parsed) ||
        !("requestor" in parsed) ||
        !("resource" in parsed) ||
        !("approval" in parsed)
      ) {
        setErrors([
          "YAML must define a routing rule with requestor, resource, and approval fields.",
        ]);
        return;
      }
      const success = await upsertRule(parsed as RoutingRule);
      if (success) navigate(`/o/${orgSlug}/${AppPaths.Policies}`);
    },
    [upsertRule, setErrors, navigate, orgSlug]
  );

  return (
    <Flex
      vertical
      className="mx-auto max-w-[min(1440px,calc(100vw-48px))] pt-4 pb-8 w-full"
    >
      <PolicyEditorHeader
        name={name ?? ""}
        disabled={disabled ?? false}
        isDirty={isDirty}
        isValid={isValidRule}
        isEditMode={isEditMode}
        isSubmitting={isSubmitting}
        mode={mode}
        onNameChange={setName}
        onDisabledChange={setDisabled}
        onSave={handleSave}
        onCancel={handleCancel}
        onModeChange={handleModeChange}
      />

      {errors.map((e, ix) => (
        <ErrorDisplay error={e} key={ix} />
      ))}

      <PolicyOutlineNav />

      <Flex vertical gap={16}>
        {mode === "advanced" ? (
          <CodeEditor
            onDirty={setYamlDirty}
            handleSubmitWorkflow={handleYamlSubmit}
            rule={selectedYamlRule}
            setDefaultRule={setDefaultRule}
            setSampleExample={setSampleExample}
          />
        ) : (
          <List>
            <Section
              title={SECTION_TITLES.requestor}
              valid={isComponentValid("requestor")}
              touched={isEditMode || requestor !== undefined}
            >
              <RequestorEditor />
            </Section>
            <Section
              title={SECTION_TITLES.resource}
              valid={isComponentValid("resource")}
              touched={isEditMode || resource !== undefined}
            >
              <ResourceEditor
                onResourceFilterEditInProgress={setResourceFilterEditInProgress}
              />
            </Section>
            <Section
              title={SECTION_TITLES.approval}
              valid={isComponentValid("approval")}
              touched={isEditMode || approval.length > 0}
            >
              <AccessEditor
                onAccessRuleEditInProgress={setAccessRuleEditInProgress}
              />
            </Section>
          </List>
        )}
      </Flex>
    </Flex>
  );
};

export const PolicyEditorPage: React.FC = () => {
  const { ruleId } = useParams<{ ruleId: string }>();
  const { currentVersion, errors, isLoading, jsonRules } =
    useRoutingRulesStore();
  const isEditMode = ruleId !== undefined;

  useLoadRoutingRules({ force: isEditMode });

  const alreadyLoaded = currentVersion !== undefined || jsonRules.length > 0;
  const ready = !isLoading && alreadyLoaded;

  // NOTE: This is a hack until we add proper stable ids to each rule.
  // Resolve the rule index from the URL hash when rules first load, and again
  // whenever `ruleId` changes (i.e. the user navigates between edit pages).
  // Saving an edited rule changes its hash but not the URL's ruleId, so the
  // latch stays stable across saves — avoiding a flash of "Rule not found"
  // between the post-save store update and the navigation back to the list.
  const resolvedRef = useRef<{
    ruleId: string | undefined;
    index: number | undefined;
  } | null>(null);
  if (ready && resolvedRef.current?.ruleId !== ruleId) {
    if (isEditMode) {
      const idx = jsonRules.findIndex((rule) => hashRule(rule) === ruleId);
      resolvedRef.current = {
        ruleId,
        index: idx === -1 ? undefined : idx,
      };
    } else {
      resolvedRef.current = { ruleId: undefined, index: undefined };
    }
  }
  const ruleIndex = resolvedRef.current?.index;

  // `useLoadRoutingRules` flips `isLoading` in an effect, so there is a brief
  // render where `isLoading` is still false even though we're about to fetch.
  // Treat that as loading to avoid flashing error states on first paint.
  if (isLoading || (!alreadyLoaded && errors.length === 0)) {
    return <Spin size="large" className="flex justify-center mt-16" />;
  }

  if (errors.length > 0 && !alreadyLoaded) {
    return (
      <Alert
        type="error"
        message="Unable to load routing rules"
        description={errors.join("\n")}
        showIcon
        className="m-6 whitespace-pre-wrap"
      />
    );
  }

  if (isEditMode && ruleIndex === undefined) {
    return (
      <Alert
        type="error"
        message="Rule not found"
        description="The routing rule you are trying to edit does not exist. It may have been deleted or the URL is invalid."
        showIcon
        className="m-6"
      />
    );
  }

  const initialRule =
    ruleIndex !== undefined ? jsonRules[ruleIndex] : undefined;

  return (
    <InstalledIntegrationsProvider>
      <RoutingRuleEditorProvider
        key={ruleId ?? "new"}
        initialRule={initialRule}
      >
        <PolicyEditorPageInner
          initialRule={initialRule}
          ruleIndex={ruleIndex}
        />
      </RoutingRuleEditorProvider>
    </InstalledIntegrationsProvider>
  );
};
